import { clean, parseCurrency, parsePrice } from './product';

/**
 * OpenGraph, and the handful of meta tags that behave like it. Every site that
 * wants to look right when pasted into a chat app has these, which is most of
 * them — so this catches nearly everything JSON-LD misses.
 */

export type OpenGraph = {
  title: string | undefined;
  description: string | undefined;
  price: number | undefined;
  currency: string | undefined;
  images: string[];
};

export function openGraph(doc: Document): OpenGraph {
  return {
    title: meta(doc, ['og:title', 'twitter:title']),
    description: meta(doc, ['og:description', 'twitter:description', 'description']),
    price: parsePrice(
      meta(doc, ['product:price:amount', 'og:price:amount', 'twitter:data1']),
    ),
    currency: parseCurrency(meta(doc, ['product:price:currency', 'og:price:currency'])),
    // Repeated `og:image` is how a listing offers its gallery.
    images: metaAll(doc, ['og:image:secure_url', 'og:image', 'twitter:image']),
  };
}

/** True when the page carried enough for this to have been worth reading. */
export function hasOpenGraph(found: OpenGraph): boolean {
  return found.title !== undefined || found.images.length > 0;
}

function meta(doc: Document, names: readonly string[]): string | undefined {
  return metaAll(doc, names)[0];
}

/**
 * `property` is the OpenGraph spelling and `name` is the HTML one; plenty of
 * sites use whichever their templating library defaulted to, so both are read.
 *
 * Names are tried in order and the first that matches wins outright — falling
 * through to `twitter:image` only matters when `og:image` is absent, not when
 * it exists and is second in document order.
 */
function metaAll(doc: Document, names: readonly string[]): string[] {
  for (const name of names) {
    const selector = `meta[property="${name}" i], meta[name="${name}" i]`;

    const values = [...doc.querySelectorAll(selector)]
      .map((tag) => clean(tag.getAttribute('content')))
      .filter((value): value is string => value !== undefined);

    if (values.length > 0) return values;
  }

  return [];
}
